import Compressor from 'compressorjs';
import { ChangeEvent, useRef } from 'react';
import { ErrorToast } from '../../../adminComponents/Toasts/Toasts';
import { UploadedImage } from '../../../adminComponents/UploadedImage/UploadedImage';
import type { MediaKind, StoredMedia } from '../helpers/announcementAudience';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { TargetChip } from './AnnouncementGroupCard.styled';
import {
  MediaColumn,
  MediaKindRow,
  MediaNote,
} from './AnnouncementMediaInput.styled';

/** The same ceiling `AdminImageInput` puts on a compressed image. */
const MAX_IMAGE_BYTES = 2097152;

const KIND_LABEL: Record<MediaKind, string> = { image: 'Image', gif: 'GIF' };
const KIND_NOUN: Record<MediaKind, string> = { image: 'image', gif: 'GIF' };
const KIND_NOUN_A: Record<MediaKind, string> = {
  image: 'an image',
  gif: 'a GIF',
};

type Props = {
  kind: MediaKind;
  media: File | Blob | string | null;
  /** What the group opened with; see `AudienceGroup.storedMedia`. */
  storedMedia: StoredMedia | null;
  /** A chip clicked: the kind changes, nothing is picked yet. */
  onKind: (kind: MediaKind) => void;
  /** A file picked (or a pick undone), with the kind it turned out to be. */
  onChange: (media: File | Blob | string | null, kind: MediaKind) => void;
};

/** What the API takes as an image; a GIF is the other kind. */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * A group's image OR GIF - since Oct 2026 a target carries exactly one, and
 * only that file is sent.
 *
 * The file decides which: pick a GIF and the group becomes a GIF group, pick
 * a PNG and it goes back to Image. The chips show the result and can still be
 * set by hand, but they are not a step to take first - a GIF greyed out in
 * the file dialog because the chip still said Image read as "GIFs are gone".
 *
 * One picker for both rather than `AdminImageInput` plus a second one: that
 * component runs every pick through compressorjs, which redraws it on a
 * canvas and keeps the first frame. An image still goes through the same
 * compression here; a GIF goes to the API exactly as picked, in `gif_…` -
 * the API answers a GIF in `image_…` with a 400.
 */
export const AnnouncementMediaInput = ({
  kind,
  media,
  storedMedia,
  onKind,
  onChange,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // A stable URL, not one per render: UploadedImage would mint a new blob URL
  // on every keystroke in the card, and a GIF would restart each time.
  const url = useObjectUrl(media);
  const isFresh = !!media && typeof media !== 'string';

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again after a removal still fires.
    e.target.value = '';
    if (!file) return;

    if (file.type === 'image/gif') return onChange(file, 'gif');

    if (!IMAGE_TYPES.includes(file.type))
      return ErrorToast('Pick a PNG, JPG, WebP or GIF');

    new Compressor(file, {
      quality: 0.8,
      maxWidth: 2500,
      maxHeight: 1500,
      convertSize: MAX_IMAGE_BYTES,
      success: (result) =>
        result.size > MAX_IMAGE_BYTES
          ? ErrorToast('Image file too big')
          : onChange(result, 'image'),
    });
  };

  const storedOtherKind =
    !isFresh && storedMedia && storedMedia.kind !== kind ? storedMedia : null;

  return (
    <MediaColumn>
      <MediaKindRow role="radiogroup" aria-label="Image or GIF">
        {(['image', 'gif'] as const).map((option) => (
          <TargetChip
            key={option}
            type="button"
            role="radio"
            aria-checked={kind === option}
            $on={kind === option}
            onClick={() => kind !== option && onKind(option)}
          >
            {KIND_LABEL[option]}
          </TargetChip>
        ))}
      </MediaKindRow>

      <UploadedImage
        image={url}
        onClick={() => inputRef.current?.click()}
        /* Undo your own pick, back to what was stored - its kind too, since
           the pick may have switched it. A stored file has no delete on the
           API: it goes when a file of either kind replaces it. */
        onDelete={
          isFresh
            ? () =>
                onChange(storedMedia?.url ?? null, storedMedia?.kind ?? kind)
            : undefined
        }
        width="100%"
        height="200px"
        heightSM="140px"
      />
      <input
        ref={inputRef}
        type="file"
        accept={[...IMAGE_TYPES, 'image/gif'].join(',')}
        onChange={pick}
      />

      {kind === 'gif' && (
        <MediaNote>
          There is no still fallback for a GIF, so give this group a minimum app
          version that can play it.
        </MediaNote>
      )}

      {storedOtherKind && (
        <MediaNote>
          The saved {KIND_NOUN[storedOtherKind.kind]} stays until you pick{' '}
          {KIND_NOUN_A[kind]} - it is replaced on save.
        </MediaNote>
      )}
    </MediaColumn>
  );
};
